import React, { useState, useMemo, useCallback } from "react";
import { GripVertical, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useSimpleTableContext } from "../../contexts/SimpleTableContext";
import { FieldHelpPopover } from "@/components/shared/FieldHelp/FieldHelpPopover";
import { ColumnHeaderContextMenu } from "./ColumnHeaderContextMenu";
import { useColumnResizeDrag } from "./use-column-resize-drag";
import type { BaseEntity, SimpleTableConfig, SimpleFieldConfig } from "../../types";
import {
  buildSortColumnMap,
  computeHeaderLabelLayout,
  isAllRowsSelected,
} from "./header-column-layout";

// Separate component for individual column headers to allow hooks at top level
interface ColumnHeaderProps<TData extends BaseEntity> {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  index: number;
  isLast: boolean;
  config: SimpleTableConfig<TData>;
  draggedColumn: string | null;
  sortColumnMap: Map<string, { direction: "asc" | "desc"; priority: number }>;
  sortField: string | null;
  sortDirection: "asc" | "desc";
  columnWidths: Record<string, number>;
  handleSort: (fieldId: string) => void;
  handleDragStartCallback: (e: React.DragEvent, fieldId: string) => void;
  handleDragOverCallback: (e: React.DragEvent) => void;
  handleDropCallback: (e: React.DragEvent, fieldId: string) => void;
  handleDragEndCallback: () => void;
  handleColumnResizeCallback: (fieldId: string, width: number) => void;
  getColumnStyleCallback: (fieldId: string, fieldConfig: SimpleFieldConfig) => React.CSSProperties;
  setIsResizing: (fieldId: string | null) => void;
}

function ColumnHeader<TData extends BaseEntity>({
  fieldId,
  fieldConfig,
  index,
  isLast,
  config,
  draggedColumn,
  sortColumnMap,
  sortField,
  sortDirection,
  columnWidths,
  handleSort,
  handleDragStartCallback,
  handleDragOverCallback,
  handleDropCallback,
  handleDragEndCallback,
  handleColumnResizeCallback,
  getColumnStyleCallback,
  setIsResizing,
}: ColumnHeaderProps<TData>) {
  // Memoize label processing per field
  const labelContent = useMemo(
    () => computeHeaderLabelLayout(fieldConfig.label),
    [fieldConfig.label]
  );

  // Memoize sort column lookup
  const sortCol = useMemo(() => {
    if (config.features?.multiColumnSort) {
      return sortColumnMap.get(fieldId);
    }
    return null;
  }, [config.features?.multiColumnSort, sortColumnMap, fieldId]);

  // Resize drag lives in its own hook: the listeners go on `document`, so the
  // teardown argument (identity-matched removal + an unmount detach) is the
  // whole story and does not belong tangled in a header cell's render.
  const handleResizeStart = useColumnResizeDrag({
    onResize: useCallback(
      (deltaX: number) => {
        const startWidth = columnWidths[fieldId] || fieldConfig.width || 150;
        handleColumnResizeCallback(fieldId, startWidth + deltaX);
      },
      [columnWidths, fieldId, fieldConfig.width, handleColumnResizeCallback],
    ),
    onDragStart: useCallback(() => setIsResizing(fieldId), [fieldId, setIsResizing]),
    onDragEnd: useCallback(() => setIsResizing(null), [setIsResizing]),
  });

  const headerLabel =
    typeof fieldConfig.label === "string" ? fieldConfig.label : String(fieldConfig.label);

  const headerCell = (
    <div
      className={cn(
        "px-3 py-2 font-semibold text-card-foreground text-xs flex items-center gap-1.5 border-r border-border relative group",
        isLast && "last:border-r-0",
        // First (primary title) column stays pinned past the 40px select cell
        // so row identity survives horizontal scroll on wide tables.
        index === 0 && "sticky left-10 z-raised bg-background",
        draggedColumn === fieldId && "opacity-50",
        config.features?.columnResizing && "cursor-move"
      )}
      style={getColumnStyleCallback(fieldId, fieldConfig)}
      draggable={config.features?.columnResizing}
      onDragStart={(e) => handleDragStartCallback(e, fieldId)}
      onDragOver={handleDragOverCallback}
      onDrop={(e) => handleDropCallback(e, fieldId)}
      onDragEnd={handleDragEndCallback}
    >
      {config.features?.columnResizing && (
        <GripVertical className="h-4 w-4 text-gray-400 cursor-move flex-shrink-0" />
      )}
      {labelContent.type === "break" ? (
        <span className={cn("flex-1 overflow-visible whitespace-normal break-words leading-snug")}>
          <span>{labelContent.firstLine}</span>
          <br />
          {labelContent.rest && <span>{labelContent.rest}</span>}
        </span>
      ) : (
        <span
          className={cn(
            "flex-1 overflow-visible",
            labelContent.enableWrap
              ? "whitespace-normal break-words leading-snug"
              : "whitespace-nowrap"
          )}
        >
          {labelContent.label}
        </span>
      )}
      <FieldHelpPopover entity={config.entity} fieldId={fieldId} side="top" align="start" />
      {fieldConfig.sortable && (
        <div className="flex items-center gap-1">
          <button type="button"
            onClick={() => handleSort(fieldId)}
            className="hover:bg-muted/80 rounded p-1 transition-colors"
          >
            {config.features?.multiColumnSort ? (
              sortCol ? (
                sortCol.direction === "asc" ? (
                  <ArrowUp className="h-4 w-4 text-blue-600" />
                ) : (
                  <ArrowDown className="h-4 w-4 text-blue-600" />
                )
              ) : (
                <ChevronsUpDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-400" />
              )
            ) : sortField === fieldId ? (
              sortDirection === "asc" ? (
                <ArrowUp className="h-4 w-4" />
              ) : (
                <ArrowDown className="h-4 w-4" />
              )
            ) : (
              <ChevronsUpDown className="h-4 w-4 text-gray-400" />
            )}
          </button>
          {config.features?.multiColumnSort && sortCol && (
            <Badge
              variant="outline"
              className="text-xs h-4 w-4 p-0 flex items-center justify-center"
            >
              {sortCol.priority}
            </Badge>
          )}
        </div>
      )}

      {/* Column Resize Handle */}
      {config.features?.columnResizing && fieldConfig.resizable !== false && (
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
          onMouseDown={handleResizeStart}
        />
      )}
    </div>
  );

  // Right-click a header for sort + column-visibility controls without opening
  // the modal. Shown when the table opts into column visibility OR the column
  // is sortable; otherwise there's nothing to offer.
  if (!config.features?.columnVisibility && !fieldConfig.sortable) {
    return headerCell;
  }

  return (
    <ColumnHeaderContextMenu<TData>
      fieldId={fieldId}
      label={headerLabel}
      sortable={!!fieldConfig.sortable}
    >
      {headerCell}
    </ColumnHeaderContextMenu>
  );
}

export function TableHeaderColumns<TData extends BaseEntity>() {
  const context = useSimpleTableContext<TData>();
  const [isResizing, setIsResizing] = useState<string | null>(null);

  const {
    config,
    visibleFields,
    state,
    paginatedData,
    callbacks,
    utils,
  } = context;

  const {
    selectedItems,
    sortColumns,
    sortField,
    sortDirection,
    draggedColumn,
    columnWidths,
  } = state;

  const {
    handleSelectAll,
    handleSort,
    handleDragStartCallback,
    handleDragOverCallback,
    handleDropCallback,
    handleDragEndCallback,
    handleColumnResizeCallback,
  } = callbacks;

  const { getColumnStyleCallback } = utils;

  // Memoize select all checked state
  const isAllSelected = useMemo(
    () => isAllRowsSelected(selectedItems.size, paginatedData.length),
    [selectedItems.size, paginatedData.length]
  );

  // Memoize sort column lookup map for better performance
  const sortColumnMap = useMemo(() => buildSortColumnMap(sortColumns), [sortColumns]);

  const containerStyle = useMemo(() => ({ minWidth: "max-content" }), []);

  return (
    <div className="bg-background border-b border-border sticky top-0 z-raised">
      <div className="flex items-center" style={containerStyle}>
        {/* Select All - sticky so row selection survives horizontal scroll */}
        <div className="w-10 px-2 py-2 flex items-center justify-center sticky left-0 z-raised bg-background border-r border-border">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-700 text-blue-600 focus:ring-blue-500"
          />
        </div>

        {/* Column Headers */}
        {visibleFields.map(([fieldId, fieldConfig], index) => (
          <ColumnHeader
            key={fieldId}
            fieldId={fieldId}
            fieldConfig={fieldConfig}
            index={index}
            isLast={index === visibleFields.length - 1}
            config={config}
            draggedColumn={draggedColumn}
            sortColumnMap={sortColumnMap}
            sortField={sortField}
            sortDirection={sortDirection}
            columnWidths={columnWidths}
            handleSort={handleSort}
            handleDragStartCallback={handleDragStartCallback}
            handleDragOverCallback={handleDragOverCallback}
            handleDropCallback={handleDropCallback}
            handleDragEndCallback={handleDragEndCallback}
            handleColumnResizeCallback={handleColumnResizeCallback}
            getColumnStyleCallback={getColumnStyleCallback}
            setIsResizing={setIsResizing}
          />
        ))}

        {/* Actions Column - Only show if actions are not inline with title */}
        {config.rowActionsPlacement !== "inlineTitle" && (
          <div className="w-16 px-3 py-2 font-semibold text-card-foreground text-xs border-r border-border">
            Actions
          </div>
        )}
      </div>
    </div>
  );
}
